import { BookOpen, Megaphone, Users } from "lucide-react";

import type { SocialAdCopy } from "@/lib/api";

import { AUDIENCE_PHRASE } from "./utils";

interface AdCopyPreviewProps {
  copy: SocialAdCopy;
  /** Shown only where the block needs naming — the plan card, the result. */
  heading?: string;
}

/**
 * The words that go on the ad, laid out the way the ad lays them out.
 *
 * A headline that carries the picture, an optional supporting line, and a call
 * to action — deliberately no name, no role, no organisation anywhere. The copy
 * is written in the VELUX tone of voice for a market and an audience rather
 * than quoted from a person.
 *
 * Who it was written for and where it was drawn from sit underneath, because
 * both are part of what someone is approving: evocative brand copy can stand on
 * its own, but anything that states a fact has to name the library document it
 * came from.
 */
export function AdCopyPreview({ copy, heading }: AdCopyPreviewProps) {
  const audience = copy.audience ? (AUDIENCE_PHRASE[copy.audience] ?? copy.audience) : undefined;

  return (
    <section className="border-border bg-card space-y-2.5 rounded-lg border p-3">
      {heading && <h4 className="type-label text-muted">{heading}</h4>}

      <div className="space-y-1.5">
        <p className="font-heading text-foreground text-base leading-6 font-bold">
          {copy.headline}
        </p>
        {copy.subline && <p className="text-foreground/85 text-sm leading-5">{copy.subline}</p>}
        {copy.cta && (
          <p className="text-velux flex items-center gap-1.5 text-xs font-semibold">
            <Megaphone className="h-3.5 w-3.5 shrink-0" />
            {copy.cta}
          </p>
        )}
      </div>

      <div className="text-faint space-y-1 text-[11px] leading-5">
        {audience && (
          <p className="flex items-start gap-1.5">
            <Users className="mt-0.5 h-3 w-3 shrink-0" />
            <span>Written for {audience}, in the VELUX tone of voice.</span>
          </p>
        )}
        <p className="flex items-start gap-1.5">
          <BookOpen className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            {/* Source is only required where the copy claims something. Saying
                so is more honest than leaving a blank line that reads as a
                missing citation. */}
            {copy.source
              ? `Drawn from ${copy.source}.`
              : "Brand copy — it makes no factual claim, so there is no document behind it."}
            {/* The backend hands this over already labelled — "Photo credit:
                Adam Mørk" — so prefixing it here would say it twice. */}
            {copy.photo_credit ? ` ${copy.photo_credit}` : ""}
          </span>
        </p>
      </div>
    </section>
  );
}
